import { useEffect, useRef } from "react";

/**
 * Embedded YouTube IFrame player. Uses the official IFrame Player API so we
 * get programmatic control + state events for our watch_events pipeline.
 *
 * Premium ad-free playback: when Doug is signed into YouTube in this browser
 * (third-party cookies allowed for accounts.google.com), Google detects his
 * Premium subscription and serves the video ad-free in the iframe. Some
 * videos may have embedding disabled by the creator — for those, the iframe
 * shows an error and the user can fall back to the "Open on YouTube.com"
 * button below.
 */

interface YouTubePlayerProps {
  videoId: string;
  onPlay?: () => void;
  onTimeUpdate?: (currentS: number, durationS: number) => void;
  onEnded?: () => void;
  autoplay?: boolean;
  /** Resume position in seconds. Applied once after the player is ready. */
  startAt?: number;
}

/* eslint-disable @typescript-eslint/no-explicit-any */
declare global {
  interface Window {
    YT?: any;
    onYouTubeIframeAPIReady?: () => void;
  }
}

let apiLoadingPromise: Promise<void> | null = null;

function loadYouTubeApi(): Promise<void> {
  if (typeof window === "undefined") return Promise.resolve();
  if (window.YT && window.YT.Player) return Promise.resolve();
  if (apiLoadingPromise) return apiLoadingPromise;

  apiLoadingPromise = new Promise((resolve) => {
    const prev = window.onYouTubeIframeAPIReady;
    window.onYouTubeIframeAPIReady = () => {
      prev?.();
      resolve();
    };
    const tag = document.createElement("script");
    tag.src = "https://www.youtube.com/iframe_api";
    tag.async = true;
    document.head.appendChild(tag);
  });
  return apiLoadingPromise;
}

export function YouTubePlayer({
  videoId,
  onPlay,
  onTimeUpdate,
  onEnded,
  autoplay = true,
  startAt = 0,
}: YouTubePlayerProps): JSX.Element {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const playerRef = useRef<any>(null);
  const tickerRef = useRef<number | null>(null);

  useEffect(() => {
    let cancelled = false;
    loadYouTubeApi().then(() => {
      if (cancelled || !containerRef.current || !window.YT?.Player) return;
      playerRef.current = new window.YT.Player(containerRef.current, {
        videoId,
        playerVars: {
          autoplay: autoplay ? 1 : 0,
          rel: 0,
          modestbranding: 1,
          playsinline: 1,
          start: startAt > 1 ? Math.floor(startAt) : undefined,
        },
        events: {
          onReady: () => {
            // Some YouTube embeds ignore the `start` playerVar; explicit seek
            // is the most reliable way to resume.
            if (startAt > 1) {
              try { playerRef.current?.seekTo?.(startAt, true); } catch { /* ignore */ }
            }
          },
          onStateChange: (e: { data: number }) => {
            // YT.PlayerState.PLAYING === 1, ENDED === 0
            if (e.data === 1) onPlay?.();
            if (e.data === 0) onEnded?.();
          },
        },
      });
    });

    // Poll the player for position updates (the API doesn't emit timeupdate).
    tickerRef.current = window.setInterval(() => {
      const p = playerRef.current;
      if (!p?.getCurrentTime || !p?.getDuration) return;
      try {
        const cur = p.getCurrentTime();
        const dur = p.getDuration();
        if (typeof cur === "number" && typeof dur === "number") {
          onTimeUpdate?.(cur, dur);
        }
      } catch {
        /* player not ready yet */
      }
    }, 1000);

    return () => {
      cancelled = true;
      if (tickerRef.current) window.clearInterval(tickerRef.current);
      try {
        playerRef.current?.destroy?.();
      } catch {
        /* ignore */
      }
    };
  }, [videoId]);

  return <div ref={containerRef} className="w-full h-full" />;
}
/* eslint-enable @typescript-eslint/no-explicit-any */

export function extractYouTubeId(url: string): string | null {
  // Handles `https://www.youtube.com/watch?v=ID`, `https://youtu.be/ID`,
  // `https://www.youtube.com/embed/ID`. Returns null otherwise.
  try {
    const u = new URL(url);
    if (u.hostname === "youtu.be") return u.pathname.replace(/^\//, "");
    if (u.hostname.endsWith("youtube.com")) {
      const v = u.searchParams.get("v");
      if (v) return v;
      const m = /^\/embed\/([^/?]+)/.exec(u.pathname);
      if (m) return m[1] ?? null;
    }
  } catch {
    /* invalid url */
  }
  return null;
}
